import {
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { ForgeContext } from './ForgeContext'
import type { Platform, RegistryData, Skill, Tool } from '../types'

export function ForgeProvider({ children }: { children: ReactNode }) {
  const [registry, setRegistry] = useState<RegistryData | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedSkills, setSelectedSkills] = useState<string[]>([])
  const [selectedWorkflow, setSelectedWorkflow] = useState<string | null>(null)
  const [platform, setPlatform] = useState<Platform>('maclinux')
  const [tool, setTool] = useState<Tool>('claude')

  useEffect(() => {
    Promise.all([
      fetch('/registry/skills.json').then((r) => r.json()),
      fetch('/registry/workflows.json').then((r) => r.json()),
      fetch('/registry/gallery.json').then((r) => r.json()),
    ])
      .then(([skills, workflows, gallery]) => {
        setRegistry({
          skills: skills.skills,
          workflows: workflows.workflows,
          gallery: gallery.gallery,
        })
      })
      .catch((err) => {
        console.error('Failed to load registry:', err)
      })
      .finally(() => setLoading(false))
  }, [])

  const toggleSkill = (id: string) => {
    setSelectedSkills((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    )
  }

  const selectWorkflow = (id: string | null) => {
    setSelectedWorkflow(id)
    if (id && registry) {
      const wf = registry.workflows.find((w) => w.id === id)
      if (wf) {
        setSelectedSkills((prev) => {
          const base = prev.filter((s) => !wf.skillIds.includes(s))
          return [...base, ...wf.skillIds]
        })
      }
    }
  }

  const clearSelection = () => {
    setSelectedSkills([])
    setSelectedWorkflow(null)
  }

  const selectedSkillObjects = useMemo(() => {
    if (!registry) return []
    return selectedSkills
      .map((id) => registry.skills.find((s) => s.id === id))
      .filter(Boolean) as Skill[]
  }, [registry, selectedSkills])

  const selectedWorkflowObject = useMemo(() => {
    if (!registry || !selectedWorkflow) return null
    return registry.workflows.find((w) => w.id === selectedWorkflow) ?? null
  }, [registry, selectedWorkflow])

  const command = useMemo(() => {
    const skillPart =
      selectedSkills.length > 0 ? ` --skills "${selectedSkills.join(',')}"` : ''
    const workflowPart = selectedWorkflow ? ` --workflow "${selectedWorkflow}"` : ''
    const envPart = ` --env ${tool}`
    const platformPart = platform === 'windows' ? ' --platform wsl' : ' --platform unix'
    return `forge install${workflowPart}${skillPart}${envPart}${platformPart}`
  }, [selectedSkills, selectedWorkflow, platform, tool])

  return (
    <ForgeContext.Provider
      value={{
        selectedSkills,
        selectedWorkflow,
        platform,
        tool,
        registry,
        loading,
        toggleSkill,
        selectWorkflow,
        setPlatform,
        setTool,
        clearSelection,
        command,
        selectedSkillObjects,
        selectedWorkflowObject,
      }}
    >
      {children}
    </ForgeContext.Provider>
  )
}
